import { isEqual } from 'lodash';

/**
 * Compares two objects and returns a new object containing only the keys
 * that have different values between the current and original objects.
 *
 * @param  currentValues - The current values object to compare.
 * @param  originalValues - The original values object to compare against.
 * @returns  - A new object with the keys that have different values in currentValues and originalValues.
 *
 */
const filterChangedValues = (currentValues: Record<string, any>, originalValues: Record<string, any>) => {
  return Object.keys(currentValues).reduce((acc: Record<string, any>, key) => {
    if (currentValues[key] !== originalValues[key]) {
      acc[key] = currentValues[key];
    }
    return acc;
  }, {});
};

const convertUrlToFile = async (url?: string, fileName?: string): Promise<File | null> => {
  if (!url || !fileName) {
    return null;
  }
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
};

/**
 * Creates a new object containing only the specified keys from the original object.
 * Only properties with defined values will be included in the resulting object.
 *
 * @template T - The type of the source object
 * @template K - Array type of keys to pick from the source object
 * @param {T} obj - The source object to pick properties from
 * @param {K} keys - An array of keys to include in the result
 * @returns {Pick<T, K[number]>} A new object containing only the specified keys
 *
 */
const pick = <T extends object, K extends readonly (keyof T)[]>(obj: T, keys: K): Pick<T, K[number]> =>
  keys.reduce((acc, k) => {
    const v = obj[k];
    if (v !== undefined) (acc as any)[k] = v;
    return acc;
  }, {} as any);

export { filterChangedValues, convertUrlToFile, pick };

// Perform deep equality check on changed values, will be replaced when model mapping is introduced (╯°□°）╯︵ ┻━┻
export const getChangedValues = <T extends Record<string, any>>(values: T, initialValues: T) =>
  Object.entries(values).reduce((changes, [key, value]) => {
    if (!isEqual(initialValues[key], value)) changes[key as keyof T] = value;
    return changes;
  }, {} as Partial<T>);

// Currency formatting utility
export const formatCurrency = (value: number | string | null | undefined, maximumFractionDigits = 2) => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : (value ?? 0);

  const safe = isNaN(num) ? 0 : num;
  const fractionDigits = Math.max(0, maximumFractionDigits);

  return safe.toLocaleString('en-AU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    style: 'currency',
    currency: 'AUD',
  });
};
